import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, rectSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { PageHeader, money, priorityColor, categoryColor, PlanBadge } from '@/components/PageHeader';
import { StickyNote } from '@/components/StickyNote';
import { InsightsBuilder } from '@/components/InsightsBuilder';
import { GripVertical, Calendar, CheckSquare, Wallet, Bell, KanbanSquare, Receipt, Plus, TrendingUp, Clock, Target, SlidersHorizontal, RotateCcw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const ALL_WIDGETS = ['commitments', 'calendar', 'tasks', 'finance', 'projects', 'receivables', 'notifications'];
const WIDGET_LABELS = {
  commitments: 'Compromisos recurrentes', calendar: 'Próximos eventos', tasks: 'Tareas pendientes',
  finance: 'Finanzas', projects: 'Proyectos activos', receivables: 'Cuentas por cobrar', notifications: 'Notificaciones',
};

const WidgetShell = ({ id, title, icon: Icon, children, onOpen }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 50 : 1 };
  return (
    <div ref={setNodeRef} style={style} data-testid={`dashboard-widget-${id}`} onClick={onOpen}
      className={`pp-glass pp-noise relative overflow-hidden p-5 cursor-pointer ${isDragging ? 'pp-glow-mint shadow-2xl' : ''}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2"><Icon size={16} className="pp-mint" /><span className="text-sm font-semibold">{title}</span></div>
        <div className="flex items-center gap-1">
          {onOpen && <span className="text-[11px] text-muted-foreground group-hover:pp-mint">Abrir →</span>}
          <button onClick={(e) => e.stopPropagation()} {...attributes} {...listeners} className="cursor-grab opacity-40 hover:opacity-90 touch-none"><GripVertical size={15} /></button>
        </div>
      </div>
      {children}
    </div>
  );
};

export default function Dashboard() {
  const { user, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ tasks: [], events: [], summary: null, projects: [], receivables: [], notifs: [], commitments: [] });
  const [order, setOrder] = useState(ALL_WIDGETS);
  const [hidden, setHidden] = useState([]);
  const [persOpen, setPersOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  // Catálogo de métricas para las gráficas configurables (Insights).
  const STATUS_LABELS = { pending: 'Pendiente', in_progress: 'En progreso', completed: 'Completado', blocked: 'Bloqueado' };
  const insightMetrics = useMemo(() => {
    const series = data.summary?.series || [];
    const m = [
      { id: 'income', label: 'Ingresos', color: '#3EB489', data: series.map((s) => ({ label: s.month, value: s.income || 0 })) },
      { id: 'expense', label: 'Egresos', color: '#C9A84C', data: series.map((s) => ({ label: s.month, value: s.expense || 0 })) },
      { id: 'net', label: 'Balance neto', color: '#A8E6CE', data: series.map((s) => ({ label: s.month, value: s.net || 0 })) },
      { id: 'payable', label: 'Cuentas por pagar', color: '#E35D6A', data: series.map((s) => ({ label: s.month, value: s.payable || 0 })) },
      { id: 'receivable', label: 'Cuentas por cobrar', color: '#7FB3A0', data: series.map((s) => ({ label: s.month, value: s.receivable || 0 })) },
      { id: 'tasks_status', label: 'Tareas por estado', color: '#3EB489', data: ['pending', 'in_progress', 'completed', 'blocked'].map((st) => ({ label: STATUS_LABELS[st], value: data.tasks.filter((t) => t.status === st).length })) },
      { id: 'projects', label: 'Proyectos (progreso)', color: '#C9A84C', data: data.projects.map((p) => ({ label: p.name, value: p.progress || 0 })) },
      { id: 'commitments', label: 'Compromisos completados', color: '#A8E6CE', data: data.commitments.map((g) => ({ label: g.name.split(' ')[0], value: g.items.filter((i) => i.done).length })) },
    ];
    return m.filter((x) => x.data.length > 0);
  }, [data]);

  const load = useCallback(async () => {
    try {
      const [t, e, s, p, r, n, sn, cm] = await Promise.all([
        api.get('/tasks'), api.get('/events'), api.get('/finance/summary').catch(() => ({ data: null })),
        api.get('/projects').catch(() => ({ data: [] })), api.get('/finance/receivables').catch(() => ({ data: [] })),
        api.get('/notifications'), api.get('/sticky-notes'),
        api.get('/commitments').catch(() => ({ data: [] })),
      ]);
      setData({ tasks: t.data, events: e.data, summary: s.data, projects: p.data, receivables: r.data, notifs: n.data, commitments: cm.data });
      setNotes(sn.data);
    } catch (e) { console.error('Error al cargar el panel:', e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Preferencias de panel POR USUARIO (persisten en users.dashboard_prefs)
  useEffect(() => {
    const prefs = user?.dashboard_prefs || {};
    const savedOrder = Array.isArray(prefs.order) ? prefs.order.filter((i) => ALL_WIDGETS.includes(i)) : [];
    const merged = [...new Set([...savedOrder, ...ALL_WIDGETS])];
    setOrder(merged);
    setHidden(Array.isArray(prefs.hidden) ? prefs.hidden.filter((i) => ALL_WIDGETS.includes(i)) : []);
  }, [user]);

  const persist = async (nextOrder, nextHidden) => {
    try { await updateProfile({ dashboard_prefs: { order: nextOrder, hidden: nextHidden } }); } catch (err) { console.error('No se pudieron guardar las preferencias del panel:', err); }
  };

  const onDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const next = arrayMove(order, order.indexOf(active.id), order.indexOf(over.id));
    setOrder(next);
    persist(next, hidden);
  };

  const toggleWidget = (id) => {
    const next = hidden.includes(id) ? hidden.filter((x) => x !== id) : [...hidden, id];
    setHidden(next); persist(order, next);
  };
  const resetPanel = () => { setOrder(ALL_WIDGETS); setHidden([]); persist(ALL_WIDGETS, []); toast.success('Panel restaurado'); };

  const addNote = async () => {
    const colors = ['mint', 'gold', 'sage', 'cream'];
    const { data: n } = await api.post('/sticky-notes', { text: '', color: colors[notes.length % 4], x: 20 + (notes.length % 3) * 30, y: 20, rotation: (Math.random() * 6 - 3) });
    setNotes([...notes, n]);
  };
  const updNote = async (n) => { setNotes(notes.map((x) => x.id === n.id ? n : x)); await api.patch(`/sticky-notes/${n.id}`, { text: n.text, color: n.color }); };
  const delNote = async (id) => { setNotes(notes.filter((x) => x.id !== id)); await api.delete(`/sticky-notes/${id}`); };

  const upcoming = [...data.events].filter((e) => new Date(e.start) >= new Date(Date.now() - 864e5)).sort((a, b) => new Date(a.start) - new Date(b.start)).slice(0, 4);
  const pending = data.tasks.filter((t) => t.status !== 'completed');

  const widgets = {
    commitments: (
      <WidgetShell id="commitments" title="Compromisos recurrentes" icon={Target} onOpen={() => navigate('/plannerapp/finance')}>
        <div className="space-y-3">
          {data.commitments.length === 0 && <p className="text-xs text-muted-foreground">Crea tus pagos recurrentes en Finanzas.</p>}
          {data.commitments.map((g) => {
            const total = g.items.length; const done = g.items.filter((i) => i.done).length;
            const pct = total ? Math.round((done / total) * 100) : 0;
            const complete = done === total && total > 0;
            return (
              <div key={g.id} className="flex items-center gap-3">
                <div className="relative w-11 h-11 shrink-0">
                  <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                    <circle cx="18" cy="18" r="15" fill="none" stroke="rgba(150,170,160,0.18)" strokeWidth="3.5" />
                    <circle cx="18" cy="18" r="15" fill="none" stroke={g.color} strokeWidth="3.5" strokeDasharray={`${pct * 0.942} 100`} strokeLinecap="round" style={{ transition: 'stroke-dasharray 0.6s ease' }} />
                  </svg>
                  <div className="absolute inset-0 grid place-items-center text-[10px] font-bold">{done}/{total}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{g.name}</div>
                  <div className="text-[11px]" style={{ color: complete ? '#3EB489' : '#C9A84C' }}>{complete ? 'Completado ✓' : `${total - done} pendientes`}</div>
                </div>
              </div>
            );
          })}
        </div>
      </WidgetShell>
    ),
    calendar: (
      <WidgetShell id="calendar" title="Próximos eventos" icon={Calendar} onOpen={() => navigate('/plannerapp/calendar')}>
        <div className="space-y-2">
          {upcoming.length === 0 && <p className="text-xs text-muted-foreground">Sin eventos próximos.</p>}
          {upcoming.map((e) => { const c = categoryColor(e.category); return (
            <div key={e.id} className="flex items-center gap-2 text-sm">
              <span className="w-2 h-2 rounded-full" style={{ background: c.text }} />
              <span className="flex-1 truncate">{e.title}</span>
              <span className="text-[11px] text-muted-foreground">{new Date(e.start).toLocaleDateString('es', { day: '2-digit', month: 'short' })}</span>
            </div>
          ); })}
        </div>
      </WidgetShell>
    ),
    tasks: (
      <WidgetShell id="tasks" title="Tareas pendientes" icon={CheckSquare} onOpen={() => navigate('/plannerapp/tasks')}>
        <div className="text-3xl font-black pp-mint mb-2">{pending.length}</div>
        <div className="space-y-1.5">
          {pending.slice(0, 3).map((t) => { const c = priorityColor(t.priority); return (
            <div key={t.id} className="flex items-center gap-2 text-sm"><span className="w-1.5 h-1.5 rounded-full" style={{ background: c.text }} /><span className="flex-1 truncate">{t.title}</span></div>
          ); })}
        </div>
      </WidgetShell>
    ),
    finance: data.summary && (
      <WidgetShell id="finance" title="Finanzas" icon={Wallet} onOpen={() => navigate('/plannerapp/finance')}>
        <div className="flex items-baseline gap-2 mb-1"><TrendingUp size={16} className="pp-mint" /><span className="text-2xl font-black">{money(data.summary.net)}</span></div>
        <div className="text-[11px] text-muted-foreground mb-2">Balance neto</div>
        <div className="h-14 min-h-[56px] w-full">
          {(data.summary.series && data.summary.series.length > 0) ? (
            <ResponsiveContainer width="100%" height={56} minWidth={0}>
              <LineChart data={data.summary.series}>
                <Line type="monotone" dataKey="income" stroke="#3EB489" strokeWidth={2} dot={false} isAnimationActive />
                <Line type="monotone" dataKey="expense" stroke="#C9A84C" strokeWidth={2} dot={false} isAnimationActive />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full grid place-items-center text-[11px] text-muted-foreground">Sin datos de tendencia</div>
          )}
        </div>
      </WidgetShell>
    ),
    projects: (
      <WidgetShell id="projects" title="Proyectos activos" icon={KanbanSquare} onOpen={() => navigate('/plannerapp/tasks')}>
        <div className="space-y-3">
          {data.projects.slice(0, 3).map((p) => (
            <div key={p.id}>
              <div className="flex justify-between text-sm mb-1"><span className="truncate">{p.name}</span><span className="text-muted-foreground">{p.progress}%</span></div>
              <div className="h-1.5 rounded-full bg-[rgba(255,255,255,0.08)]"><div className="h-full rounded-full" style={{ width: `${p.progress}%`, background: p.color }} /></div>
            </div>
          ))}
          {data.projects.length === 0 && <p className="text-xs text-muted-foreground">Sin proyectos.</p>}
        </div>
      </WidgetShell>
    ),
    receivables: (
      <WidgetShell id="receivables" title="Cuentas por cobrar" icon={Receipt} onOpen={() => navigate('/plannerapp/finance')}>
        <div className="text-2xl font-black pp-gold mb-2">{money(data.summary?.ar_due || 0)}</div>
        <div className="space-y-1.5">
          {data.receivables.slice(0, 3).map((r) => (
            <div key={r.id} className="flex justify-between text-sm"><span className="truncate">{r.client}</span><span className="text-muted-foreground">{money(r.amount)}</span></div>
          ))}
        </div>
      </WidgetShell>
    ),
    notifications: (
      <WidgetShell id="notifications" title="Notificaciones" icon={Bell} onOpen={() => navigate('/plannerapp/notifications')}>
        <div className="space-y-2">
          {data.notifs.slice(0, 3).map((n) => (
            <div key={n.id} className="text-sm"><div className="font-medium truncate flex items-center gap-1"><Clock size={11} className="pp-mint" />{n.title}</div><div className="text-[11px] text-muted-foreground truncate">{n.body}</div></div>
          ))}
          {data.notifs.length === 0 && <p className="text-xs text-muted-foreground">Todo al día.</p>}
        </div>
      </WidgetShell>
    ),
  };

  return (
    <div data-testid="dashboard-page">
      <PageHeader kicker={`Hola, ${user?.name?.split(' ')[0]}`} title="Pizarra Ejecutiva" badge={<PlanBadge plan={user?.plan} />} subtitle="Arrastra los widgets para reorganizar tu cockpit. Ancla notas físicas donde quieras."
        actions={<>
          <button data-testid="personalize-panel-btn" onClick={() => setPersOpen(true)} className="h-10 px-4 rounded-xl text-sm font-medium border flex items-center gap-2"><SlidersHorizontal size={16} /> Personalizar</button>
          <button data-testid="add-sticky-note" onClick={addNote} className="h-10 px-4 rounded-xl text-sm font-medium text-[#07090A] flex items-center gap-2" style={{ background: '#3EB489' }}><Plus size={16} /> Nota</button>
        </>} />

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={order.filter((id) => !hidden.includes(id))} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {order.filter((id) => !hidden.includes(id)).map((id) => widgets[id] ? <div key={id}>{widgets[id]}</div> : null)}
          </div>
        </SortableContext>
      </DndContext>

      {/* Personalizar panel (por usuario) */}
      <Dialog open={persOpen} onOpenChange={setPersOpen}>
        <DialogContent className="pp-glass-strong" data-testid="personalize-dialog">
          <DialogHeader><DialogTitle>Personaliza tu panel</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">Muestra u oculta widgets. Arrastra las tarjetas para reordenarlas. Tus ajustes son privados de tu usuario.</p>
          <div className="space-y-1 mt-3 max-h-[50vh] overflow-y-auto pp-scroll">
            {ALL_WIDGETS.map((id) => (
              <label key={id} className="flex items-center justify-between py-2 px-3 rounded-xl hover:bg-[rgba(255,255,255,0.03)]">
                <span className="text-sm">{WIDGET_LABELS[id]}</span>
                <Switch checked={!hidden.includes(id)} onCheckedChange={() => toggleWidget(id)} data-testid={`toggle-widget-${id}`} />
              </label>
            ))}
          </div>
          <button data-testid="reset-panel-btn" onClick={resetPanel} className="mt-2 h-10 rounded-xl border text-sm flex items-center justify-center gap-2"><RotateCcw size={15} /> Restaurar diseño original</button>
        </DialogContent>
      </Dialog>

      {/* Sticky notes board */}
      <div className="mt-8">
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">Notas ancladas</div>
        <div className="pp-glass p-6 min-h-[240px] relative overflow-hidden">
          <div className="flex flex-wrap gap-6">
            {notes.map((n) => <StickyNote key={n.id} note={n} onChange={updNote} onDelete={delNote} />)}
            {notes.length === 0 && <button onClick={addNote} className="text-sm text-muted-foreground flex items-center gap-2 hover:pp-mint"><Plus size={16} /> Añade tu primera nota física</button>}
          </div>
        </div>
      </div>

      {/* Insights: gráficas configurables por el usuario */}
      <div className="mt-8" data-testid="insights-section">
        <div className="mb-3">
          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Insights automáticos</div>
          <p className="text-[11px] text-muted-foreground mt-1">Elige qué métricas graficar (puedes cruzar varias) y cómo verlas: líneas, barras o tortas.</p>
        </div>
        <InsightsBuilder
          metrics={insightMetrics}
          storageKey={`pp_dash_charts_${user?.id || 'me'}`}
          defaults={[
            { id: 'd1', title: 'Ingresos vs Egresos', type: 'line', metrics: ['income', 'expense'] },
            { id: 'd2', title: 'Tareas por estado', type: 'pie', metrics: ['tasks_status'] },
          ]}
        />
      </div>
    </div>
  );
}
